//#region Imports
import React, { useEffect, useRef, useState } from 'react';
import { styles } from './ProductService.css';
import { styles as swatchStyles } from '../variants/swatches/Swatches.css';
import { OfferingsProps, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { ProductServiceButton } from '@jbhi-fi/components/src/Attach/ProductServiceButton/ProductServiceButton';
import {
  getPrimaryOfferingOfService,
  initPDPServiceSelections,
  isAppleCareNz,
  isExtraCare,
  isExtraCareReplacementCover,
} from '../utils/product-service';
import { ServiceIds } from '../../../../utils/constants/services';
import {
  ServicesInfoBlock,
  ServicesInfoBlockProps,
} from '@jbhi-fi/components/src/Attach/ServicesInfoBlock/ServicesInfoBlock';
import {
  ModalWrapper,
  ProductServiceModal,
} from '@jbhi-fi/components/src/Attach/ProductServiceModal/ProductServiceModal';
import {
  elementIds,
  generateProductServiceModalProps,
  generateServicesInfoBlockProps,
  getServiceButtonHeading,
} from './utils';
import { ServiceIcon } from './icons/ServiceIcon';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { applyNoWrap } from '../../../../utils/noWrap';
import { CollapsedContentBlock } from '@jbhi-fi/components/src/Attach/CollapsedContentBlock/CollapsedContentBlock';
import { ProductServiceTracking } from '../utils/ga4Tracking/trackServiceActions';
//#endregion

export type ProductServiceProps = {
  productSku: string;
  service: ServicesProps; //primary service to offer
  variant?: 'default' | 'pdp' | 'slideout';
  preSelectedOfferingId?: string;

  isColourOption?: boolean;
  shouldShowActionButtons?: boolean;
  attachProductStatus?: 'added' | 'cancelled' | 'hidden';
  handleServiceStatus?: (type: 'ms365' | 'attach') => void;

  //tracking properties
  gtmData: any;
  trackingMessage?: string;

  onAdd?: (productSku: string, serviceId: string, offeringId: string) => Promise<void>;
  onRemove?: (productSku: string, serviceId: string, offeringId: string) => Promise<void>;
};

const ProductService = ({
  productSku,
  service,
  variant = 'default',

  isColourOption,
  shouldShowActionButtons,
  preSelectedOfferingId,
  attachProductStatus,
  handleServiceStatus,

  gtmData,
  trackingMessage,

  onAdd,
  onRemove,
}: ProductServiceProps) => {
  //#region Global variables
  const isPdpOrCompare = ['pdp', 'compare'].includes(variant);
  const gtmSource = variant !== 'default' ? variant : window?.gtm?.pageType === 'Checkout Pages' ? 'cart' : 'minicart';
  const isCartPage = gtmSource === 'cart';
  const isMiniCart = gtmSource === 'minicart';
  const cartType = isCartPage ? 'cart' : isMiniCart ? 'miniCart' : undefined;
  const popUpVariants = ['popupPDP', 'popupCollection', 'popupSearch'];
  const serviceElementIds = {
    productServicesWrapper: useIdPrefix('product-services-wrapper'),
  };
  const isSingleOffer = service?.offerings?.length === 1 && !isCartPage;
  const shouldHideHeading: boolean = variant === 'default'; //hide heading in service carousel
  const isExtraCareService = isExtraCare(service);
  const isExtraCareReplacementCoverService = isExtraCareReplacementCover(service);
  const isAppleCareNzService = isAppleCareNz(service);
  const primaryOffering = getPrimaryOfferingOfService(service);
  //#endregion

  //#region Hooks
  const { isMiniCartOpen } = useCartContext();

  const {
    trackServicePreSelection,
    trackServiceDeselection,
    trackServiceAddition,
    trackServiceModalClose,
    trackButtonImpression,
  } = ProductServiceTracking({ service, gtmData, gtmSource, trackingMessage });
  //#endregion

  //#region State
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedModalId, setSelectedModalId] = useState<string>('');
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(preSelectedOfferingId);
  const [isCartReady, setIsCartReady] = useState<boolean>(true);
  //#endregion

  //#region Use Effects

  useEffect(() => {
    if (isPdpOrCompare) {
      initPDPServiceSelections();
    }
  }, [productSku, isPdpOrCompare]); //suggestions,

  useEffect(() => {
    if (modalVisible) {
      if (modalContainerRef.current) {
        const modalElement = modalContainerRef?.current?.children?.[0].children?.[0] as HTMLDivElement | undefined;
        if (modalElement) {
          modalElement.focus();
          modalElement.click();
        }
      }
    }
  }, [modalVisible]);

  useEffect(() => {
    //TODO: to understand purpose of tracking button impression when mini cart is open?
    const offering = service?.offerings?.find(offering => offering.id === selectedOfferingId);
    if (offering && gtmData) {
      if (gtmData?.source === 'minicart' && isMiniCartOpen) {
        trackButtonImpression(offering.id);
      }
      if (gtmData?.source === 'pdp' && !isMiniCartOpen) {
        trackButtonImpression(offering.id);
      }
      if (gtmData?.source === 'popup') {
        trackButtonImpression(offering.id);
      }
    }
  }, [isMiniCartOpen]);

  //#endregion

  //#region Event Handlers
  //Handler for click/check of service button, displayed in service carousel of mini cart and cart page
  const onButtonClick = (event: React.ChangeEvent | React.SyntheticEvent, offering?: OfferingsProps) => {
    event.preventDefault();
    setModalVisible(!modalVisible);
    if (offering) {
      setSelectedModalId(offering.id);
      trackServicePreSelection(offering.id);
    }
  };

  const onServiceOfferingRemoved = async (service: ServicesProps, offeringId: string) => {
    const offering = service?.offerings?.find(offering => offering.id === offeringId);

    if (service?.id && offering) {
      setIsCartReady(false);
      await onRemove?.(productSku, service.id, offering.id);
      setIsCartReady(true);
    }

    setSelected(false);
    setTimeout(() => setSelectedOfferingId(undefined), 200);
    setModalVisible(false);

    if (offering) {
      trackServiceDeselection(offering.id);
    }
  };

  const onServiceOfferingAdded = async (service: ServicesProps, offeringId: string) => {
    //if selected service differs from previously selected service, then remove previously selected offering, before adding newly selected offering
    const previouslySelectedOffering = service?.offerings?.find(o => o.id === selectedOfferingId);
    if (previouslySelectedOffering && service?.id && previouslySelectedOffering.id !== offeringId) {
      await onRemove?.(productSku, service.id, previouslySelectedOffering.id);
    }

    if (service.id && offeringId) {
      setIsCartReady(false);
      await onAdd?.(productSku, service.id, offeringId);
      setIsCartReady(true);
    }

    setSelectedOfferingId(offeringId);
    setSelected(true);
    setModalVisible(false);

    if (offeringId) {
      trackServiceAddition(offeringId);
    }
  };

  const onModalClose = (event: React.ChangeEvent | React.SyntheticEvent | KeyboardEvent, offering: OfferingsProps) => {
    setModalVisible(false);
    if (offering) {
      trackServiceModalClose(offering.id);
    }
  };
  //#endregion

  //#region Pure methods
  //TODO: wrap pure methods in useMemo hook
  const titleText = (() => {
    if (isAppleCareNzService) {
      return window.Jbhifi?.attachConfig?.servicesAppleCareNzHeading;
    }
    if (isExtraCareService) {
      return window.Jbhifi?.attachConfig?.servicesExtraCareHeading;
    }
    return applyNoWrap(window.Jbhifi?.attachConfig?.servicesJBExtrasHeading, ['JB Hi-Fi']);
  })();

  const servicesInfoBlockProps = generateServicesInfoBlockProps({
    heading: titleText,
    onAdd: onButtonClick,
    onRemove: onServiceOfferingRemoved, //TODO: to change it from onServiceDeselected to onButtonClick as we're handling selection/de-selection (via checkbox) rather than addition/removal (which happens in modal)
    service,
    isExtraCareReplacementCover: isExtraCareReplacementCoverService,
    isExtraCare: isExtraCareService,
    isAppleCareNz: isAppleCareNzService,
    selectedOfferingId,
    buttonPosition: ['slideout', ...popUpVariants].includes(variant) ? 'right' : 'bottom',
    isSlideoutOrCart: ['slideout', 'cart'].includes(variant),
    isSlideout: variant === 'slideout',
    isCartReady,
    shouldShowActionButtons: variant === 'slideout' && shouldShowActionButtons,
  });

  //#endregion

  //#region Render component

  if (!service?.offerings?.length) return <></>;

  if (['pdp', 'slideout', ...popUpVariants].includes(variant)) {
    const serviceInfoBlockType = servicesInfoBlockProps?.heading?.toLowerCase().replace(/\s/g, '_');
    return (
      <div
        id={serviceElementIds.productServicesWrapper}
        data-service-block-type={serviceInfoBlockType ?? 'standard'}
        className={
          variant === 'slideout' ? '' : `${swatchStyles.container({ isColourOption })} container-productServices`
        }
      >
        {renderServiceComponent({
          variant,
          servicesInfoBlockProps,
          attachProductStatus,
          servicePrice: `$${primaryOffering?.priceInc.toFixed(2)}`,
          handleServiceStatus,
        })}
        {service.offerings?.map(offering => (
          <ModalWrapper
            key={`${offering.id}-${offering.priceInc}`}
            onClose={e => onModalClose(e, offering)}
            isModalOpen={modalVisible ? selectedModalId === offering.id : false}
            modalDomElement={document.getElementsByClassName('body-container')?.[0]}
          >
            <ProductServiceModal
              {...generateProductServiceModalProps({
                service,
                offering,
                onAdd: onServiceOfferingAdded,
                onRemove: onServiceOfferingRemoved,
                selectedOfferingId,
                isExtraCare: isExtraCareService,
                isExtraCareReplacementCover: isExtraCareReplacementCoverService,
                isAppleCareNz: isAppleCareNzService,
                isSlideoutOrCart: variant === 'slideout',
                isCartReady,
                preSelectedOfferingId,
              })}
            />
          </ModalWrapper>
        )) ?? null}
      </div>
    );
  }
  //used in service carousel
  if (variant === 'default') {
    return (
      <>
        {!shouldHideHeading ? (
          <CalloutFont as="h3" id="service-title" className={styles.addExtendedWarrantyWords({ page: cartType })}>
            <span dangerouslySetInnerHTML={{ __html: titleText }} />
          </CalloutFont>
        ) : null}
        {!isExtraCareService && !isAppleCareNzService && !shouldHideHeading ? (
          <span>{primaryOffering?.shortDescription}</span>
        ) : null}
        <div
          className={`${styles.productOffersWrap({ shouldHideHeading })} ${isCartPage ? 'isCartPage' : ''}`}
          data-testid={`${cartType ? `${cartType}-` : ''}service-suggestion`}
        >
          {
            //TODO: consolidate ALMOST same code base for PDP variant above as single method
            service?.offerings?.map(offering => (
              <div
                className={styles.productOffer({ isSingleOffer, page: cartType, shouldHideHeading })}
                key={`${offering.id}-${offering.priceInc}`}
              >
                <ModalWrapper
                  onClose={e => onModalClose(e, offering)}
                  isModalOpen={modalVisible ? selectedModalId === offering.id : false}
                  modalDomElement={document.getElementsByClassName('body-container')?.[0]}
                >
                  <ProductServiceModal
                    {...generateProductServiceModalProps({
                      service,
                      offering,
                      onAdd: onServiceOfferingAdded,
                      onRemove: onServiceOfferingRemoved,
                      selectedOfferingId,
                      isExtraCare: isExtraCareService,
                      isExtraCareReplacementCover: isExtraCareReplacementCoverService,
                      isAppleCareNz: isAppleCareNzService,
                      isCartReady,
                      preSelectedOfferingId,
                    })}
                  />
                </ModalWrapper>
                <ProductServiceButton
                  onButtonClick={event => onButtonClick(event, offering)}
                  selected={selected ? selectedOfferingId === offering.id : false}
                  heading={getServiceButtonHeading(offering, false)}
                  price={offering.priceInc}
                  elementId={elementIds[offering.id as ServiceIds]}
                  offeringId={offering.id}
                  icon={
                    <ServiceIcon
                      imageUrl={service.imageUrl ?? offering.imageUrl ?? undefined}
                      selected={selected}
                      size={42}
                    />
                  }
                />
              </div>
            )) ?? null
          }
        </div>
      </>
    );
  }

  //#endregion
};

//#region Helper methods
const renderServiceComponent = ({
  variant,
  servicesInfoBlockProps,
  attachProductStatus,
  servicePrice,
  handleServiceStatus,
}: Pick<ProductServiceProps, 'variant' | 'attachProductStatus' | 'handleServiceStatus'> & {
  servicesInfoBlockProps?: ServicesInfoBlockProps;
  servicePrice: string;
}) => {
  if (!servicesInfoBlockProps) return null;
  if (variant !== 'slideout') {
    return <ServicesInfoBlock {...servicesInfoBlockProps} />;
  }

  if (!handleServiceStatus || !attachProductStatus) return null;

  //TODO: What's the purpose of attachProductStatus?
  if (attachProductStatus === 'hidden') {
    return <ServicesInfoBlock {...servicesInfoBlockProps} onRemove={() => handleServiceStatus('attach')} />;
  }

  return (
    <CollapsedContentBlock
      title={servicesInfoBlockProps?.heading ?? ''}
      status={attachProductStatus}
      price={servicePrice}
      onClick={() => handleServiceStatus('attach')}
    />
  );
};
//#endregion

export default ProductService;
